import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import css from './MessagingSegment.module.css';
import ForumIcon from '@mui/icons-material/Forum';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function initials(name = '') {
  const parts = String(name).trim().split(/\s+/);
  return `${(parts[0] || '')[0] || ''}${(parts[1] || '')[0] || ''}`.toUpperCase() || '?';
}

function fmtTime(d) {
  if (!d) return '';
  const date = new Date(d);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay
    ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

// Resolves a display name + subtitle for a conversation from the current actor's POV.
function conversationDisplay(conversation, actorType, actorId) {
  if (conversation.type === 'announcement') {
    return { name: conversation.subject || 'Announcement', sub: `Announcement · ${conversation.audience || ''}` };
  }
  if (conversation.type === 'class_group') {
    return { name: conversation.subject || 'Class Group', sub: 'Class group' };
  }
  const other = (conversation.participants || []).find(
    p => !(p.actorType === actorType && String(p.actorId) === String(actorId))
  );
  return { name: other?.name || 'Conversation', sub: '' };
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Shared Messages tab UI for all 4 school actor dashboards (Admin/Teacher/Parent/Student).
 * The backend controller (school-messaging-controller.js) is actor-agnostic — this
 * component mirrors that by taking a `fetchFn` + `basePath` and doing everything else
 * the same way regardless of which dashboard renders it.
 *
 * @param {function} fetchFn        — the dashboard's own fetch helper (schoolFetch/teacherFetch/parentFetch/studentFetch)
 * @param {string}   basePath       — e.g. '/school', '/teacher', '/parent', '/student'
 * @param {string}   actorType      — 'school_admin' | 'teacher' | 'parent' | 'student'
 * @param {string}   currentActorId — the logged-in actor's own _id (string)
 * @param {boolean}  managementEnabled — Premium gating flag (same convention as Calendar/Attendance)
 * @param {boolean}  canCreateClassGroup — whether this actor may start class_group threads (admin/teacher)
 * @param {boolean}  canCreateAnnouncement — whether this actor may send announcements (admin only)
 * @param {Array}    classOptions   — [{ _id, name }] used for the class_group picker (only needed if canCreateClassGroup)
 */
export default function MessagingSegment({
  fetchFn,
  basePath,
  actorType,
  currentActorId,
  managementEnabled,
  canCreateClassGroup = false,
  canCreateAnnouncement = false,
  classOptions = [],
}) {
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [composerText, setComposerText] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const threadBodyRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const res = await fetchFn(`${basePath}/messages`);
      const data = await res.json();
      if (res.ok) setConversations(data.conversations || []);
    } catch { /* soft-fail */ }
    finally { setLoadingList(false); }
  }, [fetchFn, basePath]);

  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    setLoadingThread(true);
    try {
      const res = await fetchFn(`${basePath}/messages/${conversationId}`);
      const data = await res.json();
      if (res.ok) {
        setActiveConversation(data.conversation);
        setMessages(data.messages || []);
        // Optimistically clear the unread badge for this conversation in the list
        setConversations(prev => prev.map(c => String(c._id) === String(conversationId) ? { ...c, unreadCount: 0 } : c));
      }
    } catch { /* soft-fail */ }
    finally { setLoadingThread(false); }
  }, [fetchFn, basePath]);

  useEffect(() => {
    if (!managementEnabled) { setLoadingList(false); return; }
    loadConversations();
  }, [managementEnabled, loadConversations]);

  useEffect(() => {
    if (activeId) loadMessages(activeId);
  }, [activeId, loadMessages]);

  useEffect(() => {
    if (threadBodyRef.current) threadBodyRef.current.scrollTop = threadBodyRef.current.scrollHeight;
  }, [messages]);

  // Live updates via SSE — reuses the same per-actor connection as in-app notifications.
  useEffect(() => {
    if (!managementEnabled) return undefined;
    const token = localStorage.getItem('school-token');
    if (!token) return undefined;
    let url = `${basePath}/messages-stream?token=${encodeURIComponent(token)}`;
    if (actorType === 'student') {
      const schoolId = localStorage.getItem('school-id');
      if (schoolId) url += `&schoolId=${encodeURIComponent(schoolId)}`;
    }
    let es;
    try { es = new EventSource(url); } catch { return undefined; }
    es.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data || '{}');
        if ((data?.type === 'notification' || data?.type === 'notification.created') && data.notification?.meta?.conversationId) {
          loadConversations();
          const convId = data.notification.meta.conversationId;
          setActiveId(current => {
            if (current && String(current) === String(convId)) loadMessages(convId);
            return current;
          });
        }
      } catch { /* ignore malformed frame */ }
    };
    es.onerror = () => { /* browser auto-reconnects */ };
    return () => { try { es.close(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managementEnabled, basePath, actorType]);

  const handleSend = async (e) => {
    e.preventDefault();
    const body = composerText.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    try {
      const res = await fetchFn(`${basePath}/messages/${activeId}`, { method: 'POST', body: JSON.stringify({ body }) });
      const data = await res.json();
      if (res.ok) {
        setMessages(prev => [...prev, data.message]);
        setComposerText('');
        loadConversations();
      }
    } catch { /* soft-fail */ }
    finally { setSending(false); }
  };

  const canPostInActive = activeConversation
    ? activeConversation.type !== 'announcement' || (
      activeConversation.createdBy?.actorType === actorType &&
      String(activeConversation.createdBy?.actorId) === String(currentActorId)
    )
    : false;

  if (!managementEnabled) {
    return (
      <div className={css.wrap}>
        <div className={css.lockBanner}>
          🔒 Messaging is a <strong>Premium</strong> feature. Ask your school admin to upgrade the plan to unlock it.
        </div>
      </div>
    );
  }

  return (
    <div className={css.wrap}>
      <div className={css.headerRow}>
        <h2 className={css.headerTitle}>Messages</h2>
        <button className={css.newBtn} onClick={() => setShowNewModal(true)}>
          <ForumIcon style={{ fontSize: '1rem' }} /> New Message
        </button>
      </div>

      <div className={css.layout}>
        <div className={css.list}>
          {loadingList ? (
            <div className={css.listEmpty}>Loading…</div>
          ) : conversations.length === 0 ? (
            <div className={css.listEmpty}>No conversations yet. Start one with "New Message".</div>
          ) : (
            conversations.map(c => {
              const { name, sub } = conversationDisplay(c, actorType, currentActorId);
              return (
                <div
                  key={c._id}
                  className={`${css.listItem} ${String(activeId) === String(c._id) ? css.listItemActive : ''}`}
                  onClick={() => setActiveId(c._id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && setActiveId(c._id)}
                >
                  <div className={css.listAvatar}>{initials(name)}</div>
                  <div className={css.listItemBody}>
                    <div className={css.listItemTop}>
                      <span className={css.listItemName}>
                        {c.type === 'announcement' && <span className={`${css.typeBadge} ${css.typeBadgeAnnouncement}`}>Announcement</span>}
                        {c.type === 'class_group' && <span className={`${css.typeBadge} ${css.typeBadgeGroup}`}>Group</span>}
                        {name}
                      </span>
                      <span className={css.listItemTime}>{fmtTime(c.lastMessageAt)}</span>
                    </div>
                    <div className={css.listItemPreview}>{sub ? `${sub} · ` : ''}{c.lastMessagePreview}</div>
                  </div>
                  {c.unreadCount > 0 && <span className={css.unreadBadge}>{c.unreadCount > 99 ? '99+' : c.unreadCount}</span>}
                </div>
              );
            })
          )}
        </div>

        <div className={css.thread}>
          {!activeId ? (
            <div className={css.threadEmpty}>Select a conversation, or start a new one.</div>
          ) : loadingThread && !activeConversation ? (
            <div className={css.threadEmpty}>Loading…</div>
          ) : (
            <>
              <div className={css.threadHeader}>
                <div className={css.threadTitle}>{conversationDisplay(activeConversation || {}, actorType, currentActorId).name}</div>
                <div className={css.threadSub}>{conversationDisplay(activeConversation || {}, actorType, currentActorId).sub}</div>
              </div>
              <div className={css.threadBody} ref={threadBodyRef}>
                {messages.map(m => {
                  const mine = m.senderType === actorType && String(m.senderId) === String(currentActorId);
                  return (
                    <div key={m._id} className={`${css.bubbleRow} ${mine ? css.bubbleRowMine : css.bubbleRowTheirs}`}>
                      {!mine && <div className={css.bubbleSender}>{m.senderName}</div>}
                      <div className={`${css.bubble} ${mine ? css.bubbleMine : css.bubbleTheirs}`}>{m.body}</div>
                      <div className={css.bubbleTime}>{fmtTime(m.createdAt)}</div>
                    </div>
                  );
                })}
              </div>
              {canPostInActive ? (
                <form className={css.composer} onSubmit={handleSend}>
                  <textarea
                    className={css.composerInput}
                    placeholder="Type a message…"
                    value={composerText}
                    onChange={e => setComposerText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); }
                    }}
                  />
                  <button type="submit" className={css.sendBtn} disabled={sending || !composerText.trim()}>Send</button>
                </form>
              ) : (
                <div className={css.readonlyNotice}>Only the sender can post further messages in this announcement.</div>
              )}
            </>
          )}
        </div>
      </div>

      {showNewModal && (
        <NewConversationModal
          fetchFn={fetchFn}
          basePath={basePath}
          canCreateClassGroup={canCreateClassGroup}
          canCreateAnnouncement={canCreateAnnouncement}
          classOptions={classOptions}
          onClose={() => setShowNewModal(false)}
          onCreated={(conversation) => {
            setShowNewModal(false);
            loadConversations();
            setActiveId(conversation._id);
          }}
        />
      )}
    </div>
  );
}

// ─── New conversation modal ───────────────────────────────────────────────────

function NewConversationModal({ fetchFn, basePath, canCreateClassGroup, canCreateAnnouncement, classOptions, onClose, onCreated }) {
  const [type, setType] = useState('direct');
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [recipient, setRecipient] = useState(null);
  const [classId, setClassId] = useState('');
  const [groupAudience, setGroupAudience] = useState('both');
  const [audience, setAudience] = useState('all');
  const [subject, setSubject] = useState('');
  const [initialMessage, setInitialMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchFn(`${basePath}/messages/contacts`)
      .then(async r => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Could not load contacts.');
        setContacts(data.contacts || []);
      })
      .catch(error => setError(error.message || 'Could not load contacts.'))
      .finally(() => setLoadingContacts(false));
  }, [fetchFn, basePath]);

  const availableTypes = useMemo(() => {
    const t = [{ key: 'direct', label: 'Direct' }];
    if (canCreateClassGroup) t.push({ key: 'class_group', label: 'Class Group' });
    if (canCreateAnnouncement) t.push({ key: 'announcement', label: 'Announcement' });
    return t;
  }, [canCreateClassGroup, canCreateAnnouncement]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!initialMessage.trim()) { setError('A message is required.'); return; }
    if (type === 'direct' && !recipient) { setError('Select a recipient.'); return; }
    if (type === 'class_group' && !classId) { setError('Select a class.'); return; }

    setSubmitting(true);
    try {
      const body = { type, initialMessage: initialMessage.trim(), subject: subject.trim() };
      if (type === 'direct') {
        body.recipientActorType = recipient.actorType;
        body.recipientActorId = recipient.actorId;
      } else if (type === 'class_group') {
        body.classId = classId;
        body.groupAudience = groupAudience;
      } else if (type === 'announcement') {
        body.audience = audience;
      }
      const res = await fetchFn(`${basePath}/messages/conversations`, { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (res.ok) onCreated(data.conversation);
      else setError(data.error || 'Could not start conversation.');
    } catch {
      setError('Connection error.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={`${css.modal} BubblePopIn`} onClick={e => e.stopPropagation()}>
        <h3 className={css.modalTitle}>New Message</h3>

        {availableTypes.length > 1 && (
          <div className={css.typeTabs}>
            {availableTypes.map(t => (
              <button
                key={t.key}
                type="button"
                className={`${css.typeTab} ${type === t.key ? css.typeTabActive : ''}`}
                onClick={() => setType(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {error && <div className={css.modalError}>{error}</div>}

        <form onSubmit={handleSubmit}>
          {type === 'direct' && (
            <div className={css.field}>
              <label className={css.fieldLabel}>Recipient</label>
              <div className={css.contactList}>
                {loadingContacts ? (
                  <div className={css.contactItem}>Loading contacts…</div>
                ) : contacts.length === 0 ? (
                  <div className={css.contactItem}>No contacts available.</div>
                ) : (
                  contacts.map(c => (
                    <div
                      key={`${c.actorType}:${c.actorId}`}
                      className={`${css.contactItem} ${recipient && recipient.actorId === c.actorId && recipient.actorType === c.actorType ? css.contactItemActive : ''}`}
                      onClick={() => setRecipient(c)}
                    >
                      <span className={css.contactName}>{c.name}</span>
                      <span className={css.contactMeta}>{c.actorType.replace('_', ' ')}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {type === 'class_group' && (
            <>
              <div className={css.field}>
                <label className={css.fieldLabel}>Class</label>
                <select className={css.select} value={classId} onChange={e => setClassId(e.target.value)}>
                  <option value="">Select a class…</option>
                  {classOptions.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div className={css.field}>
                <label className={css.fieldLabel}>Audience</label>
                <div className={css.radioRow}>
                  <label className={css.radioOption}><input className="schoolRadio" type="radio" checked={groupAudience === 'students'} onChange={() => setGroupAudience('students')} /> Students</label>
                  <label className={css.radioOption}><input className="schoolRadio" type="radio" checked={groupAudience === 'parents'} onChange={() => setGroupAudience('parents')} /> Parents</label>
                  <label className={css.radioOption}><input className="schoolRadio" type="radio" checked={groupAudience === 'both'} onChange={() => setGroupAudience('both')} /> Both</label>
                </div>
              </div>
              <div className={css.field}>
                <label className={css.fieldLabel}>Subject (optional)</label>
                <input className={css.textInput} value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. JSS2 Excursion" />
              </div>
            </>
          )}

          {type === 'announcement' && (
            <>
              <div className={css.field}>
                <label className={css.fieldLabel}>Audience</label>
                <div className={css.radioRow}>
                  <label className={css.radioOption}><input className="schoolRadio" type="radio" checked={audience === 'teachers'} onChange={() => setAudience('teachers')} /> Teachers</label>
                  <label className={css.radioOption}><input className="schoolRadio" type="radio" checked={audience === 'students'} onChange={() => setAudience('students')} /> Students</label>
                  <label className={css.radioOption}><input className="schoolRadio" type="radio" checked={audience === 'parents'} onChange={() => setAudience('parents')} /> Parents</label>
                  <label className={css.radioOption}><input className="schoolRadio" type="radio" checked={audience === 'all'} onChange={() => setAudience('all')} /> Everyone</label>
                </div>
              </div>
              <div className={css.field}>
                <label className={css.fieldLabel}>Subject</label>
                <input className={css.textInput} value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Mid-term Break Notice" />
              </div>
            </>
          )}

          <div className={css.field}>
            <label className={css.fieldLabel}>Message</label>
            <textarea className={css.textarea} value={initialMessage} onChange={e => setInitialMessage(e.target.value)} placeholder="Write your message…" />
          </div>

          <div className={css.modalFooter}>
            <button type="button" className={css.btnGhost} onClick={onClose}>Cancel</button>
            <button type="submit" className={css.btnPrimary} disabled={submitting}>{submitting ? 'Sending…' : 'Send'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
